export type ColsMobileType =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26;

export type ColsDesktopType = ColsMobileType | 27 | 28 | 29 | 30 | 31;

export type ColsDashboardType =
  | ColsDesktopType
  | 32
  | 33
  | 34
  | 35
  | 36
  | 37
  | 38
  | 39
  | 40;

export type ColsObjectType<Mobile, Desktop> = {
  xs: Mobile;
  sm?: Desktop;
  md?: Desktop;
  lg?: Desktop;
};

export type ColsType<Mobile, Desktop> =
  | Mobile
  | ColsObjectType<Mobile, Desktop>;

export type GridColsType<Mobile, Desktop> =
  | Mobile
  | 'auto'
  | {
      xs: Mobile | 'auto';
      sm?: Desktop | 'auto';
      md?: Desktop | 'auto';
      lg?: Desktop | 'auto';
    };

export type PositionMobileType =
  | '1/1'
  | '1/2'
  | '2/2'
  | '1/3'
  | '2/3'
  | '3/3'
  | '1/4'
  | '2/4'
  | '3/4'
  | '4/4';

export type PositionDesktopType =
  | PositionMobileType
  | '1/5'
  | '2/5'
  | '3/5'
  | '4/5'
  | '5/5'
  | '1/6'
  | '2/6'
  | '3/6'
  | '4/6'
  | '5/6'
  | '6/6';

export type PositionType = {
  xs: PositionMobileType;
  sm: PositionDesktopType;
  md?: PositionDesktopType;
  lg?: PositionDesktopType;
};

export const MOBILE_GUTTER = 2;
export const DESKTOP_GUTTER = 1;

export const MOBILE_COLS: ColsMobileType = 26;
export const DESKTOP_COLS: ColsDesktopType = 31;
export const DASHBOARD_COLS: ColsDashboardType = 40;

export const getColsNum = (
  breakpoint: 'xs' | 'sm' | 'md' | 'lg',
  cols?: ColsType<ColsDashboardType, ColsDashboardType>,
) => {
  if (!cols) {
    return -1;
  }
  if (typeof cols === 'number' || typeof cols === 'string') {
    return cols;
  }
  return cols[breakpoint] || -1;
};

export const getFlex = (total: number, cols?: number, offset?: number) => {
  let styles = '';

  if (cols && cols > 0) {
    const width = `${Math.floor((cols / total) * 10e7) / 10e5}%`;

    styles = styles.concat(`
      flex-basis: ${width};
      flex-grow: 0;
      max-width: ${width};
    `);
  }
  if (offset && offset > 0) {
    styles = styles.concat(`
      margin-left: ${Math.floor((offset / total) * 10e7) / 10e5}%;
    `);
  }

  return styles;
};

export const getGrid: (cols?: number, startAt?: number | string) => string = (
  cols,
  startAt = 'auto',
) => {
  let styles = '';

  if (startAt && (typeof startAt === 'string' || startAt > 0)) {
    styles = styles.concat(`
      -ms-grid-column: ${startAt};
      grid-column-start: ${startAt};
    `);
  }
  if (cols && cols > 0) {
    styles = styles.concat(`
      -ms-grid-row-span: ${cols};
      grid-column-end: span ${cols};
    `);
  }

  return styles;
};

export const getColsForPosition = (
  totalCols: number,
  position?: PositionDesktopType,
  isDesktop?: boolean,
) => {
  if (!position || totalCols <= 0) {
    return undefined;
  }
  const posValues = position.split('/');
  // Second value is the total amount of items per row
  const totalItems = Number(posValues[1]);

  let gutter = MOBILE_GUTTER;

  if (isDesktop) {
    gutter = DESKTOP_GUTTER;
  }

  // Calculate how many columns the item width should have
  let itemCols = (totalCols - gutter * (totalItems + 1)) / totalItems;

  if (isDesktop && totalItems === 4) {
    // Calculate item styles for this special case (there is an extra columns at each edge)
    itemCols = (totalCols - 2 - gutter * (totalItems + 1)) / totalItems;
  }

  return itemCols;
};

export const getItemsPerRowFlex = (
  totalCols: number,
  position: PositionDesktopType,
  posOffset?: PositionDesktopType,
  isDesktop?: boolean,
) => {
  const posValues = position.split('/');
  // First value in fraction is the item order position
  const itemOrder = Number(posValues[0]);
  // Second value is the total amount of items per row
  const totalItems = Number(posValues[1]);

  let gutter = MOBILE_GUTTER;

  if (isDesktop) {
    gutter = DESKTOP_GUTTER;
  }

  // Calculate how many columns the item width should have
  const itemCols = getColsForPosition(totalCols, position, isDesktop);

  let offset: number;

  // If a custom offset is provided, it needs to be calculated based on the position provided
  if (posOffset) {
    const offsetValues = posOffset.split('/');
    // First value in fraction is the count of items to be left blank
    const offsetCount = Number(offsetValues[0]);
    // Second value is the total amount of items per row
    const offsetTotal = Number(offsetValues[1]);

    let offsetGutter = MOBILE_GUTTER;

    if (isDesktop) {
      offsetGutter = DESKTOP_GUTTER;
    }

    // Calculate how many columns the item width should have
    let offsetItemCols =
      (totalCols - offsetGutter * (offsetTotal + 1)) / offsetTotal;

    if (isDesktop && offsetTotal === 4) {
      // Set the item offsets for this special case (there is an extra columns at each edge)
      offsetItemCols =
        (totalCols - 2 - offsetGutter * (offsetTotal + 1)) / offsetTotal;
    }

    offset = offsetCount * offsetItemCols + offsetCount * (offsetGutter + 1);
  } else {
    // Set the item offsets based only on gutters
    offset = gutter;
    if (isDesktop && totalItems === 4) {
      // Set the item offsets for this special case (there is an extra columns at each edge)
      offset = itemOrder === 1 ? gutter + 1 : gutter;
    }
  }

  return getFlex(totalCols, itemCols, offset);
};

export const getItemsPerRowGrid = (
  totalCols: number,
  position: PositionDesktopType,
  isDesktop?: boolean,
) => {
  const posValues = position.split('/');
  // First value in fraction is the item order position
  const itemOrder = Number(posValues[0]);
  // Second value is the total amount of items per row
  const totalItems = Number(posValues[1]);

  let gutter = MOBILE_GUTTER;

  if (isDesktop) {
    gutter = DESKTOP_GUTTER;
  }

  // Calculate how many columns the item width should have
  const itemCols = getColsForPosition(totalCols, position, isDesktop) || 0;

  // Calculate where the grid item should start based on order position
  let itemStart = 1 + itemOrder * gutter + (itemOrder - 1) * itemCols;

  if (isDesktop && totalItems === 4) {
    itemStart = 2 + itemOrder * gutter + (itemOrder - 1) * itemCols;
  }

  return getGrid(itemCols, itemStart);
};
